import React, { useRef, useState } from 'react';
import { Image, Platform, View } from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import type { Screenshot, Selection } from '../../services/feedback/screenshot';
import { imageStyle, makeStyles, selectionStyle } from './style';

export function normalizeSelection(start: { x: number; y: number }, end: { x: number; y: number }): Selection | null {
  const clamp = (n: number) => Math.max(0, Math.min(1, n));
  const x = Math.min(clamp(start.x), clamp(end.x));
  const y = Math.min(clamp(start.y), clamp(end.y));
  const width = Math.abs(clamp(start.x) - clamp(end.x));
  const height = Math.abs(clamp(start.y) - clamp(end.y));
  return width < 0.01 || height < 0.01 ? null : { x, y, width, height };
}
export default function ScreenshotPreview({ screenshot, selection, onSelect }: {
  screenshot: Screenshot; selection: Selection | null; onSelect: (selection: Selection | null) => void;
}) {
  const styles = makeStyles(useTheme());
  const start = useRef<{ x: number; y: number } | null>(null);
  const [layout, setLayout] = useState({ width: 1, height: 1 });
  const point = (event: any) => ({ x: event.nativeEvent.locationX / layout.width, y: event.nativeEvent.locationY / layout.height });
  const keyboard = Platform.OS === 'web' ? {
    tabIndex: 0 as const,
    onKeyDown: (event: any) => {
      if (!['Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete', 'Backspace'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Delete' || event.key === 'Backspace') { onSelect(null); return; }
      const rect = selection || { x: 0.2, y: 0.2, width: 0.4, height: 0.4 };
      const dx = event.key === 'ArrowLeft' ? -0.02 : event.key === 'ArrowRight' ? 0.02 : 0;
      const dy = event.key === 'ArrowUp' ? -0.02 : event.key === 'ArrowDown' ? 0.02 : 0;
      onSelect(event.shiftKey ? {
        ...rect, width: Math.max(0.02, Math.min(1 - rect.x, rect.width + dx)),
        height: Math.max(0.02, Math.min(1 - rect.y, rect.height + dy)),
      } : {
        ...rect, x: Math.max(0, Math.min(1 - rect.width, rect.x + dx)),
        y: Math.max(0, Math.min(1 - rect.height, rect.y + dy)),
      });
    },
  } : {};
  return (
    <View {...keyboard} accessibilityLabel="Screenshot area selector" style={[styles.preview, imageStyle(screenshot.width || 1, screenshot.height || 1)]}
      onLayout={event => setLayout(event.nativeEvent.layout)}
      onStartShouldSetResponder={() => Platform.OS === 'web'}
      onResponderGrant={event => { start.current = point(event); onSelect(null); }}
      onResponderMove={event => { if (start.current) onSelect(normalizeSelection(start.current, point(event))); }}
      onResponderRelease={event => { if (start.current) onSelect(normalizeSelection(start.current, point(event))); start.current = null; }}
      onResponderTerminate={() => { start.current = null; }}>
      <View pointerEvents="none" style={styles.image}><Image source={{ uri: screenshot.uri }} style={styles.image} accessibilityLabel="Screenshot to send" /></View>
      {selection && <View pointerEvents="none" style={[styles.selection, selectionStyle(selection)]} />}
    </View>
  );
}
